import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ChangeDetectorRef,
  AfterViewInit,
} from '@angular/core';
import { FilesService } from '../../../services/files.service';
import {
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
  UntypedFormBuilder,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment';
import { TranslocoService } from '@ngneat/transloco';
import { ObservationsService } from '../../../services/Observations/observations.service';
import { Project360Service } from '../../../modules/p-dashboard/project360.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AccessLevel } from '../../../core/user/user.model';
import { ObservationDetails, ObservationPermissions } from '../../../models/observations.model';
import { Attachment } from '../../../models/files.model';
import { ActiveMember } from '../../../models/members.model';
import { UserAssignees } from '../../../models/assignees.model';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
} from '../confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FilePreviewerDialogComponent } from '../../../modules/p-dashboard/file-previewer-dialog/file-previewer-dialog.component';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import Quill from 'quill';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { UserService } from 'app/core/user/user.service';
import { RelatedLogsDialogComponent } from '../related-logs-dialog/related-logs-dialog.component';

// Custom Fields imports
import { CustomFieldRendererService } from '../../../services/custom-field-renderer.service';
import { ObservationCustomFieldsService } from '../../../services/observation-custom-fields.service';
import {
  CustomField,
  CustomFieldWithValue,
  CustomFieldsFormGroup,
} from '../../../models/custom-fields.model';

// Quill Editor Service imports
import {
  QuillEditorService,
  EditorConfig,
  EditorLimits,
} from '../../../services/quill-editor.service';
import { getObservationPlaceholderImage } from '../../utils/branded-placeholder.util';
import { isAiGenerated, ppeConfidencePercent } from '../../../services/PPE/ppe.models';

@Component({
  selector: 'app-shared-observation-details',
  templateUrl: './shared-observation-details.component.html',
  styleUrls: ['./shared-observation-details.component.scss'],
})
export class SharedObservationDetailsComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges
{
  @Input() observationDetails: ObservationDetails | any = {} as ObservationDetails;
  @Input() preview: boolean = false;
  @Input() permissions: any = {};
  @Input() minDate: Date;
  @Input() projectId: string;
  @Input() customFields: CustomFieldWithValue[] = [];
  @Input() isLoadingCustomFields: boolean = false;
  @ViewChild('titleValue') title: ElementRef<HTMLElement>;
  @ViewChild('descriptionValue') description: ElementRef<HTMLElement>;
  @ViewChild('recommendedAction') recommendedAction: ElementRef<HTMLElement>;
  @ViewChild('categoriesVal') categoriesVal: any;
  @ViewChild('childCategoriesVal') childCategoriesVal: any;
  @Output() onEditObservationStatus = new EventEmitter();
  @Output() editModeEmitter = new EventEmitter();
  @Output() onEditObservationPriority = new EventEmitter();
  @Output() onEditObservationDescription = new EventEmitter();
  @Output() onEditObservationPrivacy = new EventEmitter();
  @Output() onEditObservationCategories = new EventEmitter();
  @Output() onEditObservationAttachments = new EventEmitter();
  @Output() onEditObservationEvidence = new EventEmitter();
  @Output() onEditObservationTitle = new EventEmitter();
  @Output() onEditObservationMembers = new EventEmitter();
  @Output() onEditObservationDueDate = new EventEmitter<{ date: string }>();
  @Output() onEditObservationSignificance = new EventEmitter();
  @Output() onEditObservationRecommendedAction = new EventEmitter();
  @Output() onEditObservationType = new EventEmitter();
  @Output() onEditObservationCode = new EventEmitter();
  @Output() onEditObservationHashtags = new EventEmitter<string[]>();
  @Output() onEditCustomFields = new EventEmitter<{ [key: string]: any }>();
  currentDueDate = new UntypedFormControl(new Date());
  userName: string = '';
  setDate: Date;
  formattedDate: string;
  changeStatus: boolean = false;
  changeSignificance: boolean = false;
  changeDueDate: boolean = false;
  changeMembers: boolean = false;
  changeHashtags: boolean = false;
  selectedObservationHashtags: string[] = [];
  isSavingHashtags = false;
  changePrivacy: boolean = false;
  confirmButton: boolean = false;
  changeType: boolean = false;
  changeCode: boolean = false;
  isEditing: boolean = false;
  observationStatus = [];
  allObservationStatuses: { name: string; value: string; disabled?: boolean }[] = [];
  observationSignificances = [];
  observationCodes = [];
  observationTypes = [];
  configurableFields: any[] = [];

  observationPrivacy = ['public', 'private'];
  defaultStatus = { name: 'Completed', value: 'completed' };
  validatedStatus: string = '';

  defaultDate: any = new Date();
  isUserOnOfMembers: UserAssignees = null;
  observationMembers: any[];
  projectActiveMembers: ActiveMember[];
  selectedObservationMembers: any = [];
  imageNaturalWidth: number;
  imageNaturalHeight: number;
  changeTitle: boolean = false;
  changeCategories: boolean = false;
  categories: any = [];
  selectedCategories: any = [];
  observationChildCategories: any = [];
  selectedChildCategories: any = [];

  // Custom Fields Properties
  customFieldsObservable: Observable<CustomField[]>;
  private customFieldsSubscription?: Subscription;
  private customFieldsLoadVersion = 0;
  editingFields: Set<string> = new Set();
  customFieldsForm: UntypedFormGroup & CustomFieldsFormGroup;
  // Track non-editable standard observation fields (from related_fields)
  nonEditableFields: Set<string> = new Set();

  // WIR_LOGS non-editable fields configuration
  private static readonly WIR_LOGS_NON_EDITABLE_FIELDS = {
    // Standard observation fields
    standard: ['description', 'categories'],
    // Custom fields
    custom: [
      'wir_logs_parent',
      'wir_statement_number',
      'wir_inspection_and_test_plan_number',
      'wir_inspection_and_test_title',
      'wir_inspection_checklist_number',
      'wir_reference_drawing_number',
      'other_discipline',
      'wir_inspection_schedule_time',
      'wir_works_quantity_DA',
      'wir_logs_inspection_level',
    ],
    // All fields combined for convenience
    get all(): string[] {
      return [...this.standard, ...this.custom];
    },
  };

  // MIR_LOGS non-editable fields configuration
  private static readonly MIR_LOGS_NON_EDITABLE_FIELDS = {
    // Standard observation fields
    standard: ['description', 'categories'],
    // Custom fields
    custom: [
      'mir_logs_parent',
      'mir_logs_inspection_schedule_time',
      'mir_inspection_location_description',
      'other_discipline',
    ],
    // All fields combined for convenience
    get all(): string[] {
      return [...this.standard, ...this.custom];
    },
  };

  // Editor form and configuration
  editorForm: UntypedFormGroup;
  editorConfig: EditorConfig;
  changeDescription: boolean = false;
  changeRecommendedAction: boolean = false;
  changeAttachments: boolean = false;
  changeEvidenceAttachment: boolean = false;
  fileOptionsAttachments = {
    maxFilesLimit: 10,
    maxFileSize: 200,
    allowedExtensions: this._project.allowedExtensions,
  };
  fileOptions = {
    maxFilesLimit: 10,
    maxFileSize: 200,
    allowedExtensions: this._project.allowedExtensions,
  };
  attachments: any[] = [];
  evidenceAttachment: any[] = [];

  isLoading: boolean = true;
  searchControl = new FormControl('');
  filteredUsers: ActiveMember[];
  protected _onDestroy = new Subject<void>();
  nwc_client: boolean = false;

  // Get title max length based on client type
  get titleMaxLength(): number {
    return this.nwc_client ? 128 : 200;
  }

  // Resolved project id for child hashtag picker API
  get hashtagsApiProjectId(): string | undefined {
    return this.projectId || this._project?.id || this.observationDetails?.project?.project_id;
  }

  quillConfig = {
    toolbar: [['bold'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']],
  };
  // Quill editor instances - populated via onEditorCreated callback
  descriptionEditor: Quill | null = null;
  recommendedActionEditor: Quill | null = null;
  @ViewChild('dialogTemplateDesc') dialogTemplateDesc!: TemplateRef<any>;
  @ViewChild('dialogTemplateRecc') dialogTemplateRecc!: TemplateRef<any>;
  dialogRef: any;
  noteTypeMessage: string = this.translateService.translate(
    "The type of 'Note' observations cannot be changed.",
  );

  constructor(
    private filesService: FilesService,
    private observationsService: ObservationsService,
    private _project: Project360Service,
    private userService: UserService,
    private _transloco: TranslocoService,
    public dialog: MatDialog,
    private _toaster: ToastrService,
    public translateService: TranslocoService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private customFieldRenderer: CustomFieldRendererService,
    private observationCustomFields: ObservationCustomFieldsService,
    private fb: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private quillEditorService: QuillEditorService,
  ) {
    this.iconRegistry.addSvgIcon(
      'your_icon',
      this.sanitizer.bypassSecurityTrustResourceUrl('path_to_your_svg'),
    );
  }

  protected readonly AccessLevel = AccessLevel;
  protected readonly ObservationPermissions = ObservationPermissions;

  /**
   * Check if Record No should be displayed for the current observation
   * Only displays for NWC clients and observation types that support it
   */
  shouldShowRecordNo(): boolean {
    if (!this.nwc_client || !this.observationDetails) return false;
    const type = this.observationDetails.type_label || this.observationDetails.type;
    return this.observationCustomFields.shouldDisplayRecordNo(type);
  }

  /**
   * Check if Sequence No should be displayed for the current observation
   * Only displays for NWC clients and observation types that support it
   */
  shouldShowSequenceNo(): boolean {
    if (!this.nwc_client || !this.observationDetails) return false;
    const type = this.observationDetails.type_label || this.observationDetails.type;
    return this.observationCustomFields.shouldDisplaySequenceNo(type);
  }

  toInteger(value: number): number {
    return +Number(value).toFixed(0) || 0;
  }

  /**
   * Check if Total Record Rev# should be displayed for the current observation
   * Only displays for NWC clients and MIR/WIR observation types
   */
  shouldShowTotalRecordRev(): boolean {
    if (!this.nwc_client || !this.observationDetails) return false;
    const type = this.observationDetails.type_label || this.observationDetails.type;
    return this.observationCustomFields.shouldDisplayTotalRecordRev(type);
  }

  /**
   * Check if the full "Total Record Rev#" label should be shown
   * Returns true for MIR and WIR parent observations
   */
  shouldShowFullRecordRevLabel(): boolean {
    if (!this.observationDetails) return false;
    const type = this.observationDetails.type_label || this.observationDetails.type;
    return this.observationCustomFields.shouldShowFullRecordRevLabel(type);
  }

  /**
   * Check if the short "Rev#" label should be shown
   * Returns true for MIR_LOGS and WIR_LOGS
   */
  shouldShowShortRevLabel(): boolean {
    if (!this.observationDetails) return false;
    const type = this.observationDetails.type_label || this.observationDetails.type;
    return this.observationCustomFields.shouldShowShortRevLabel(type);
  }

  /**
   * Check if Unifier Observation Link should be displayed for the current observation
   * Only displays for NWC clients
   */
  shouldShowUnifierLink(): boolean {
    if (!this.nwc_client || !this.observationDetails) return false;
    // Show for all NWC observations (no type restriction needed per requirements)
    return true;
  }

  /**
   * Open Unifier observation link in a new tab
   */
  openUnifierLink(url: string): void {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  // Flag to track if the component has been initialized with observationDetails
  private _initialized = false;

  /**
   * Detect when inputs change, especially observationDetails
   * This is critical because observationDetails is set AFTER component initialization
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['observationDetails']) {
      const previousId = changes['observationDetails'].previousValue?.id;
      const currentId = changes['observationDetails'].currentValue?.id;
      if (previousId && currentId && previousId !== currentId) {
        this.changeHashtags = false;
        this.selectedObservationHashtags = [];
      }

      const currentValue = changes['observationDetails'].currentValue;
      const currentType = currentValue?.type;
      const previousType = changes['observationDetails'].previousValue?.type;

      if (currentValue?.configurable_fields) {
        this.configurableFields = currentValue.configurable_fields;
      }

      // If observationDetails is now available and we haven't initialized yet, do it now
      if (currentValue?.id && !this._initialized) {
        this.initializeWithObservationDetails();
      } else if (currentValue?.id && this._initialized) {
        // Already initialized — refresh the cached validated status so UI reflects
        // the latest status from the refetched observationDetails.
        this.computeValidatedStatus();
      }

      // If observation type changed and is valid, reload custom fields
      if (currentType && currentType !== previousType) {
        console.log('setup custom fields/categories on changes');
        this.getObservationCategories().then(() => {
          this.setupRelatedNwcLogic();
          this.setupCustomFields();
        });
      }
    }
  }

  async ngOnInit(): Promise<void> {
    await this.getObservationSignificancesAndTypes();

    // Store configurable fields from observation details if available
    if (this.observationDetails?.configurable_fields) {
      this.configurableFields = this.observationDetails.configurable_fields;
    }
    // Only setup custom fields if observationDetails already has a type
    // Otherwise, ngOnChanges will handle it when observationDetails is set
    if (this.observationDetails?.type) {
      console.log('setup custom fields/categories on init');
      this.getObservationCategories().then(() => {
        this.setupRelatedNwcLogic();
        this.setupCustomFields();
      });
    }

    // Store configurable fields from observation details if available
    if (this.observationDetails?.configurable_fields) {
      this.configurableFields = this.observationDetails.configurable_fields;
    }

    //Add search to users list
    this.searchControl.valueChanges
      .pipe(debounceTime(700), takeUntil(this._onDestroy))
      .subscribe((val) => {
        if (val.toString().length > 0) {
          this.filteredUsers = this.projectActiveMembers?.filter(
            (user) =>
              user?.name.toLowerCase()?.includes(val.toLowerCase()) ||
              user?.email.toLowerCase()?.includes(val.toLowerCase()),
          );
        } else if (val.toString().length === 0) {
          this.filteredUsers = [...this.projectActiveMembers];
        }
      });
  }

  isFieldVisible(fieldName: string): boolean {
    if (!this.configurableFields || this.configurableFields.length === 0) {
      // If no configurable_fields, show all fields by default
      return true;
    }
    const fieldConfig = this.configurableFields.find((field) => field.attribute === fieldName);
    // If field is not in configurable_fields, show it by default
    // If field is in configurable_fields, use its visible property
    return fieldConfig ? fieldConfig.visible !== false : true;
  }

  async ngAfterViewInit(): Promise<void> {
    // If observationDetails is already available, initialize now
    // Otherwise, ngOnChanges will handle it when the data arrives
    if (this.observationDetails?.id && !this._initialized) {
      this.initializeWithObservationDetails();
    }
  }

  /**
   * Initialize the component with observationDetails data.
   * This is called either from ngAfterViewInit (if data is already available)
   * or from ngOnChanges (when data arrives asynchronously).
   */
  private async initializeWithObservationDetails(): Promise<void> {
    // Prevent multiple initializations
    if (this._initialized) {
      return;
    }
    this._initialized = true;

    this.isLoading = true;
    this.editModeEmitter.emit(true);

    // Load dynamic statuses based on observation type
    await this.getObservationStatuses();

    // Use projectId input if available, fallback to _project.id, then observation's project_id
    const projectIdToUse =
      this.projectId || this._project.id || this.observationDetails?.project?.project_id;

    if (projectIdToUse) {
      const res = await this.observationsService.projectActiveMembers(projectIdToUse).toPromise();
      this.projectActiveMembers = res.data;
      this.filteredUsers = this.projectActiveMembers;
    } else {
      console.warn('No project ID available to fetch active members');
      this.projectActiveMembers = [];
      this.filteredUsers = [];
    }

    this.fileOptions.allowedExtensions = this._project.allowedExtensions; // for evidence
    this.fileOptionsAttachments.allowedExtensions = this._project.allowedExtensions
      ?.filter((value: string) => value !== 'kmz')
      ?.map((ext: string) => (ext != 'kmz' ? ext : ''));

    if (this.observationDetails.due_date == null) {
      this.observationDetails.due_date = 'N/A'.toUpperCase();
    } else {
      this.currentDueDate = new UntypedFormControl(new Date(this.observationDetails.due_date));
    }

    const user = await this.userService.getCurrentUser();

    this.isUserOnOfMembers = this.observationDetails.user_assignees?.find(
      (v) => v.user_email === user.email,
    );

    // Initialize editor form and configuration
    this.initializeEditorForm();

    // Apply status permissions after user check
    this.applyStatusPermissionsAfterUserCheck();

    this.selectedObservationMembers = this.observationDetails.user_assignees?.map((m) => m.user_id);

    this._setMinDate();
    this.defaultStatus = this.allObservationStatuses?.filter(
      (e) => e.value === this.observationDetails?.status,
    )[0];
    this.computeValidatedStatus();
    this.userName = user.name;

    this.isLoading = false;
  }

  private setupRelatedNwcLogic() {
    // Handle categories based on client type
    this.nwc_client = this._project.isNwcClientSync();

    if (this.nwc_client) {
      // NWC clients: single category
      this.selectedCategories = this.observationDetails?.categories?.[0]?.category_id || null;
    } else {
      // Non-NWC clients: multiple categories
      this.selectedCategories =
        this.observationDetails?.categories?.map((cat) => cat.category_id) || [];
    }

    // Set selected child categories based on client type
    if (this.nwc_client) {
      this.selectedChildCategories =
        this.observationDetails?.sub_categories?.[0]?.category_id || null;
    } else {
      this.selectedChildCategories =
        this.observationDetails?.sub_categories?.map((cat) => cat.category_id) || [];
    }

    // Update child categories based on selected parent categories
    this.updateChildCategories();
  }

  async getObservationSignificancesAndTypes(): Promise<void> {
    try {
      const res = await firstValueFrom(this.observationsService.observationSignificancesAndTypes());

      this.observationTypes = res?.data?.observation_types;

      this.observationSignificances = res?.data?.observation_significance;
      this.observationCodes = res?.data?.observation_code.sort((a, b) => a.order - b.order);
      this.observationSignificances = this.observationSignificances?.filter(
        (item) => item.order != 0,
      );
      if (this.observationDetails?.type) {
        this.getObservationCategories();
      } else {
        console.warn('No observation type found yet');
      }
    } catch (error) {
      console.error('Error fetching observation significances and types:', error);
    }
  }

  async getObservationCategories(): Promise<void> {
    try {
      const res: any = await firstValueFrom(
        this.observationsService.observationCategories(this.observationDetails?.type),
      );
      this.categories = res?.observation_categories;

      // Expose categories as labeled options for custom fields dependencies
      const categoriesOptions = this.categories.map((category) => ({
        label: this.getCategoryDisplayName(category),
        value: category.id,
        children: category.children,
      }));

      // Update custom fields form metadata if it exists
      if (this.customFieldsForm) {
        this.customFieldsForm.metadata = {
          ...this.customFieldsForm.metadata,
          categories: categoriesOptions,
        };
      }

      // Update child categories based on current selected categories
      this.updateChildCategories();
    } catch (error) {
      console.error('Error fetching observation categories:', error);
    }
  }

  async getObservationStatuses(): Promise<void> {
    if (!this.observationDetails?.type) {
      this.observationStatus = [];
      return;
    }

    try {
      const res: any = await firstValueFrom(
        this.observationsService.observationStatuses(this.observationDetails?.type),
      );

      const statuses = res?.observation_statuses || [];

      this.allObservationStatuses = statuses.map((status: any) => ({
        name: status.name,
        value: status.value,
        disabled: false,
      }));
      this.observationStatus = this.allObservationStatuses.map((s) => ({ ...s }));

      if (this.observationStatus.length === 0) {
        this.setupDefaultStatuses();
      }

      // Apply permission-based restrictions
      this.applyStatusPermissions();

      this.defaultStatus =
        this.allObservationStatuses?.find((e) => e.value === this.observationDetails?.status) ||
        this.allObservationStatuses?.[0];

      this.validateInitialStatus();
    } catch (error) {
      console.error('Error fetching observation statuses:', error);
      this.setupDefaultStatuses();

      this.defaultStatus =
        this.allObservationStatuses?.find((e) => e.value === this.observationDetails?.status) ||
        this.allObservationStatuses?.[0];

      this.validateInitialStatus();
    }
  }

  private setupDefaultStatuses(): void {
    this.allObservationStatuses = [
      { name: 'Pending', value: 'pending' },
      { name: 'In Progress', value: 'in_progress' },
      { name: 'Completed', value: 'completed' },
      { name: 'Closed', value: 'closed' },
    ];

    if (this.observationDetails?.permissions?.includes(AccessLevel.CAN_CLOSE_OBSERVATION)) {
      this.observationStatus = this.allObservationStatuses.map((s) => ({ ...s }));
    } else {
      this.observationStatus = this.allObservationStatuses
        .filter((s) => s.value !== 'closed')
        .map((s) => ({ ...s }));
    }
  }

  /**
   * Computes and stores the validated status for display in the template.
   * Call this after observationStatus and defaultStatus are ready.
   */
  private computeValidatedStatus(): void {
    if (!this.observationDetails?.status) {
      this.validatedStatus = this.defaultStatus?.value || '';
      return;
    }

    // Validate against the full canonical list, not the permission-filtered dropdown list.
    if (this.allObservationStatuses && this.allObservationStatuses.length > 0) {
      const isValid = this.observationsService.validateObservationStatus(
        this.observationDetails.status,
        this.allObservationStatuses,
      );

      if (!isValid) {
        this.validatedStatus = this.defaultStatus?.value || '';
        return;
      }
    }

    this.validatedStatus = this.observationDetails.status;
  }

  /**
   * Validates the initial status from observation details on component initialization
   */
  private validateInitialStatus(): void {
    if (
      !this.observationDetails?.status ||
      !this.allObservationStatuses ||
      this.allObservationStatuses.length === 0
    ) {
      return;
    }

    const isValid = this.observationsService.validateObservationStatus(
      this.observationDetails.status,
      this.allObservationStatuses,
    );

    if (!isValid) {
      console.warn(
        `Invalid initial status '${this.observationDetails.status}' for observation ${this.observationDetails.id}. ` +
          `Available statuses: ${this.allObservationStatuses.map((s) => s.value).join(', ')}`,
      );

      // Set a fallback status if available
      if (this.allObservationStatuses.length > 0) {
        this.observationDetails.status = this.allObservationStatuses[0].value;
        this.defaultStatus = this.allObservationStatuses[0];
      }
    }
  }

  updateChildCategories(): void {
    // Filter child categories based on selected parent category/categories
    this.observationChildCategories = [];

    if (this.selectedCategories) {
      // Handle both single value (NWC) and array (Non-NWC)
      const selectedCategoryIds = Array.isArray(this.selectedCategories)
        ? this.selectedCategories
        : [this.selectedCategories];

      // Collect all child categories from selected parent categories
      selectedCategoryIds.forEach((categoryId) => {
        const selectedCategory = this.categories.find((category) => category.id === categoryId);

        if (selectedCategory && selectedCategory.children && selectedCategory.children.length > 0) {
          // Avoid duplicates when adding child categories
          selectedCategory.children.forEach((child) => {
            if (!this.observationChildCategories.find((existing) => existing.id === child.id)) {
              this.observationChildCategories.push(child);
            }
          });
        }
      });
    }
  }

  onCategorySelectionChange(): void {
    // Clear selected child categories when parent category changes
    // Reset based on client type: null for NWC (single select), empty array for Non-NWC (multi-select)
    this.selectedChildCategories = this.nwc_client ? null : [];
    this.updateChildCategories();

    // Update custom fields form when categories change
    // This is critical for dependency resolution
    if (this.customFieldsForm && this.categories) {
      // Update metadata
      const categoriesOptions = this.categories.map((category) => ({
        label: this.getCategoryDisplayName(category),
        value: category.id,
        children: category.children,
      }));

      this.customFieldsForm.metadata = {
        ...this.customFieldsForm.metadata,
        categories: categoriesOptions,
      };

      // Update the virtual categories control value
      // This allows custom fields that depend on "categories" to resolve properly
      const categoriesControl = this.customFieldsForm.get('categories');
      if (categoriesControl) {
        categoriesControl.setValue(this.selectedCategories, { emitEvent: false });
      }

      // Re-evaluate all custom fields that have dependencies
      // This ensures fields that depend on categories show/hide correctly
      this.reEvaluateCustomFieldDependencies();
    }
  }

  private applyStatusPermissions(): void {
    const hasChangeStatus = this.observationDetails?.permissions?.includes(
      ObservationPermissions.CHANGE_OBSERVATION_STATUS,
    );
    const hasCanClose = this.observationDetails?.permissions?.includes(
      ObservationPermissions.CAN_CLOSE_OBSERVATION,
    );

    // If user has ONLY CAN_CLOSE_OBSERVATION (without CHANGE_OBSERVATION_STATUS)
    // Show all statuses but enable ONLY "Closed"
    if (hasCanClose && !hasChangeStatus) {
      this.observationStatus = this.observationStatus.map((status) => ({
        ...status,
        disabled: status.value !== 'closed',
      }));
      return;
    }

    // If user has CHANGE_OBSERVATION_STATUS but NOT CAN_CLOSE_OBSERVATION
    // Show all statuses but disable "Closed"
    if (hasChangeStatus && !hasCanClose) {
      this.observationStatus = this.observationStatus.map((status) => ({
        ...status,
        disabled: status.value === 'closed',
      }));
      return;
    }

    // If user has BOTH permissions
    // Show all statuses, all enabled
    if (hasChangeStatus && hasCanClose) {
      this.observationStatus.forEach((s) => (s.disabled = false));
      return;
    }

    // If user has NEITHER permission (shouldn't reach here as button is disabled)
    // But handle it anyway - disable all options
    if (this.isUserOnOfMembers) {
      this.observationStatus = this.observationStatus.map((status) => ({
        ...status,
        disabled: status.value === 'pending',
      }));
    }
  }

  private applyStatusPermissionsAfterUserCheck(): void {
    const hasChangeStatus = this.observationDetails?.permissions?.includes(
      ObservationPermissions.CHANGE_OBSERVATION_STATUS,
    );
    const hasCanClose = this.observationDetails?.permissions?.includes(
      ObservationPermissions.CAN_CLOSE_OBSERVATION,
    );

    // If user has ONLY CAN_CLOSE_OBSERVATION (without CHANGE_OBSERVATION_STATUS)
    // Show all statuses but enable ONLY "Closed"
    if (hasCanClose && !hasChangeStatus) {
      this.observationStatus = this.observationStatus.map((status) => ({
        ...status,
        disabled: status.value !== 'closed',
      }));
      return;
    }

    // If user has CHANGE_OBSERVATION_STATUS but NOT CAN_CLOSE_OBSERVATION
    // Show all statuses but disable "Closed"
    if (hasChangeStatus && !hasCanClose) {
      this.observationStatus = this.observationStatus.map((status) => ({
        ...status,
        disabled: status.value === 'closed',
      }));
      return;
    }

    // If user has BOTH permissions
    // Show all statuses, all enabled
    if (hasChangeStatus && hasCanClose) {
      this.observationStatus.forEach((s) => (s.disabled = false));
      return;
    }

    // If user has NEITHER permission (shouldn't reach here as button is disabled)
    // But handle it anyway - disable all options
    if (this.isUserOnOfMembers) {
      this.observationStatus = this.observationStatus.map((status) => ({
        ...status,
        disabled: status.value === 'pending',
      }));
    }
  }

  async ngOnDestroy() {
    this.customFieldsSubscription?.unsubscribe();

    await this._project.fetchObservations(this._project.currentPImg).catch((error) => {
      this._toaster.error(error.error.message);
    });

    // Clean up custom fields observables
    this._onDestroy.next();
    this._onDestroy.complete();
    this.editModeEmitter.emit(false);
  }

  editObservationStatus(status: any): void {
    if (typeof status !== 'string') {
      this.changeStatus = false;

      if (!this.nwc_client) {
        this.isEditing = false;
      }

      return;
    }

    // Validate that the status is one of the available statuses
    if (!this.observationsService.validateObservationStatus(status, this.observationStatus)) {
      this._toaster.error(
        this._transloco.translate('Invalid status selected. Please choose a valid status.'),
      );
      this.changeStatus = false;
      if (!this.nwc_client) {
        this.isEditing = false;
      }
      return;
    }

    if (status === 'closed' && !this.observationDetails.evidence_file) {
      const dialogData = new ConfirmDialogModel(
        this._transloco.translate('Close-Out Evidence'),
        this._transloco.translate(
          `Are you sure you want to close the observation without close-out evidence?`,
        ),
        false,
        false,
        null,
        this._transloco.translate('No, want to attach an evidence'),
        this._transloco.translate('Yes, close the observation'),
        'center',
      );

      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        maxWidth: '640px',
        minWidth: '300px',
        data: dialogData,
      });

      dialogRef.afterClosed().subscribe(async (res) => {
        if (!res) {
          this.onEditObservationStatus.emit(status);
          this.observationDetails.status = status;
          this.computeValidatedStatus();

          this.changeStatus = false;
        } else {
          this.changeStatus = false;
        }
      });
    } else {
      this.onEditObservationStatus.emit(status);
      this.observationDetails.status = status;
      this.computeValidatedStatus();

      this.changeStatus = false;
    }
  }

  async editObservationType(value: any) {
    this.onEditObservationType.emit({ type: value });
    this.observationDetails.type = value;
    // Reset child categories when type changes
    this.observationDetails.sub_categories = [];
    this.selectedChildCategories = null;
    this.getObservationCategories().then(() => {
      this.setupRelatedNwcLogic();
      this.setupCustomFields();
    });
    await this.getObservationStatuses();
    this.computeValidatedStatus();

    // Update editor configuration with new observation type
    this.updateEditorConfig();

    this.changeType = false;
    if (!this.nwc_client) {
      this.isEditing = true;
    }
  }

  /**
   * Check if a specific observation type should be disabled for selection
   * NWC-only types are disabled for non-NWC clients
   */
  isObservationTypeDisabled(observationType: string): boolean {
    if (!this.nwc_client) {
      // NWC clients can select all types
      return false;
    }

    // Non-NWC clients cannot select NWC-only types
    return this.observationCustomFields.isCategoryTypeUpdatable(observationType);
  }

  /**
   * Initialize editor form and configuration for QuillEditorService
   */
  private initializeEditorForm(): void {
    this.editorForm = this.fb.group({
      description: [this.observationDetails?.description || ''],
      recommended_corrective_action: [this.observationDetails?.recommended_corrective_action || ''],
    });

    this.editorConfig = {
      nwcClient: this.nwc_client,
      observationType: this.observationDetails?.type || '',
      form: this.editorForm,
      observationCustomFields: this.observationCustomFields,
    };
  }

  /**
   * Update editor configuration when observation type changes
   */
  private updateEditorConfig(): void {
    if (this.editorConfig) {
      this.editorConfig.observationType = this.observationDetails?.type || '';

      // Force editor length limits with new configuration
      if (this.descriptionEditor) {
        this.quillEditorService.forceEditorLength(
          this.descriptionEditor,
          'description',
          this.editorConfig,
        );
      }

      if (this.recommendedActionEditor) {
        this.quillEditorService.forceEditorLength(
          this.recommendedActionEditor,
          'recommendedAction',
          this.editorConfig,
        );
      }
    }
  }

  /**
   * Check if the entire type dropdown should be disabled
   * This happens when an NWC-only type is already selected
   */
  isTypeDropdownDisabled(): boolean {
    if (!this.observationDetails?.type) {
      return false;
    }

    if (!this.nwc_client) {
      return false;
    }

    // is category type changable
    return this.observationCustomFields.isCategoryTypeUpdatable(this.observationDetails.type);
  }

  /**
   * Check if type editing should be completely disabled for NWC clients
   */
  isTypeEditingDisabledForNWC(): boolean {
    return this.nwc_client;
  }

  /**
   * Check if status editing should be completely disabled for NWC clients
   * Status editing is allowed for specific observation types
   */
  isStatusEditingDisabledForNWC(): boolean {
    return this.observationCustomFields.isStatusEditingDisabledForNWC(
      this.observationDetails?.type,
      this.nwc_client,
    );
  }

  editObservationSignificance(significance: any): void {
    this.onEditObservationSignificance.emit({ significance: significance });
    this.observationDetails.significance = significance;

    this.changeSignificance = false;
    if (!this.nwc_client) {
      this.isEditing = true;
    }
  }

  editObservationDescription(description: any): void {
    if (this.nwc_client && (!description || description.trim() === '')) {
      this._toaster.error(
        this.translateService.translate('Description is required for NWC clients'),
      );
      this.changeDescription = false;
      if (!this.nwc_client) {
        this.isEditing = false;
      }
      return;
    }

    this.onEditObservationDescription.emit({ description: description });
    this.observationDetails.description = description;
    this.changeDescription = false;

    if (!this.nwc_client) {
      this.isEditing = true;
    }
  }

  editObservationRecommendedAction(recommendedAction: any): void {
    this.onEditObservationRecommendedAction.emit({
      recommended_corrective_action: recommendedAction,
    });
    this.observationDetails.recommended_corrective_action = recommendedAction;
    this.changeRecommendedAction = false;

    if (!this.nwc_client) {
      this.isEditing = true;
    }
  }

  getDate(value: any): void {
    this.setDate = new Date(value);
    const currentUserTime = moment(value).unix();
    const currentTimePlusHour = moment().add(0.99, 'hours').unix();
    if (currentUserTime <= currentTimePlusHour) {
      const msg = this.translateService.translate(
        'Due time must be at least more than one hour from current time!',
      );
      this._toaster.warning(msg);
      this.currentDueDate = new UntypedFormControl('');
    } else {
      //correcting time zone to Riyadh
      this.formattedDate = value;
    }
  }

  editObservationDueDate(): void {
    // this.formattedDate=this.globalService.correctingTimeZone(this.formattedDate)
    if (!this.nwc_client) {
      this.isEditing = true;
    }
    this.changeDueDate = false;
    this.observationDetails.due_date = this.formattedDate;
    this.onEditObservationDueDate.emit({ date: this.formattedDate });
  }

  downloadFile(attache: any): void {
    this.filesService.download(attache.path, attache.file_name);
  }

  downloadEvidence(evidence): void {
    this.filesService.download(evidence, evidence.substring(evidence?.lastIndexOf('/') + 1));
  }

  async doChangeMembers(): Promise<void> {
    const isAssigneesRequired = !this.nwc_client && this.observationDetails?.type !== 'note';

    if (this.selectedObservationMembers.length !== 0 || !isAssigneesRequired) {
      const updateObservationResponse = await this.observationsService
        .updateObservation({
          observation_id: this.observationDetails?.id,
          users: this.selectedObservationMembers,
          project_id: this._project?.id,
        })
        .toPromise()
        .catch((reason) => {
          this._toaster.error(reason.error.message);
          this.changeMembers = false;
        });
      if (updateObservationResponse) {
        this._toaster.success(this.translateService.translate('Observation successfully updated'));
        this.observationDetails.user_assignees = this.selectedObservationMembers;

        this.observationsService
          .getObservationDetails(this.observationDetails?.id)
          .subscribe((res: any) => {
            if (res) {
              this.observationDetails.user_assignees_str = res.observation_details.user_assignees
                ?.map((user: any) => user.user_name)
                .join(',');
            }
          });

        this.changeMembers = false;

        this.onEditObservationMembers.emit(this.observationDetails.user_assignees);
      }
    } else {
      this._toaster.error(this.translateService.translate('This field cannot be empty'));
    }
  }

  getDisplayHashtags(): string[] {
    return this.normalizeHashtagsList(this.observationDetails?.hashtags);
  }

  getNormalizedHashtagsSnapshot(): string[] {
    return [...this.getDisplayHashtags()];
  }

  startChangeHashtags(): void {
    this.selectedObservationHashtags = this.getNormalizedHashtagsSnapshot();
    this.changeHashtags = true;
    this.editModeEmitter.emit(true);
    this.startEditing();
  }

  onObservationHashtagsDraftChange(tags: string[]): void {
    this.selectedObservationHashtags = tags;
  }

  cancelChangeHashtags(): void {
    this.changeHashtags = false;
    this.stopEditing();
  }

  async saveObservationHashtags(): Promise<void> {
    if (this.isSavingHashtags) {
      return;
    }
    const projectId =
      this.projectId || this._project?.id || this.observationDetails?.project?.project_id;
    if (!this.observationDetails?.id || !projectId) {
      this._toaster.error(
        this.translateService.translate('Something went wrong, please try again later.'),
      );
      return;
    }
    this.isSavingHashtags = true;
    const hashtags = this.dedupeHashtagsCaseInsensitive(
      this.selectedObservationHashtags.map((t) => this.normalizeHashtagToken(t)).filter((t) => !!t),
    );
    try {
      await firstValueFrom(
        this.observationsService.updateObservation({
          observation_id: this.observationDetails.id,
          project_id: projectId,
          hashtags,
        }),
      );
      this.observationDetails.hashtags = [...hashtags];
      this._toaster.success(this.translateService.translate('Observation successfully updated'));
      this.changeHashtags = false;
      this.stopEditing();
      this.onEditObservationHashtags.emit(hashtags);
    } catch (reason: any) {
      const msg =
        reason?.error?.message ||
        (Array.isArray(reason?.error?.errors) ? reason.error.errors.join(', ') : null) ||
        this.translateService.translate('Something went wrong, please try again later.');
      this._toaster.error(msg);
    } finally {
      this.isSavingHashtags = false;
    }
  }

  private normalizeHashtagsList(raw: unknown): string[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    const out: string[] = [];
    for (const item of raw) {
      if (typeof item === 'string') {
        const n = this.normalizeHashtagToken(item);
        if (n) {
          out.push(n);
        }
      }
    }
    return this.dedupeHashtagsCaseInsensitive(out);
  }

  private normalizeHashtagToken(s: string): string {
    const t = (s || '').trim();
    if (!t) {
      return '';
    }
    return t.startsWith('#') ? t : '#' + t;
  }

  private dedupeHashtagsCaseInsensitive(tags: string[]): string[] {
    const seen = new Set<string>();
    const uniq: string[] = [];
    for (const tag of tags) {
      const k = tag.toLowerCase();
      if (!seen.has(k)) {
        seen.add(k);
        uniq.push(tag);
      }
    }
    return uniq;
  }

  adjustDotPosition($event: Event) {
    const imageElement = event.target as HTMLImageElement;
    this.imageNaturalWidth = imageElement.naturalWidth;
    this.imageNaturalHeight = imageElement.naturalHeight;
    const dotElement = document.querySelector('.dot') as HTMLElement;
    let dotLeft = 0;
    let dotTop = 0;
    if (this.observationDetails.observation_flag === 'observation_on_floor_plan') {
      dotLeft = (this.observationDetails.location_x / this.imageNaturalWidth) * 100;
      dotTop = (this.observationDetails.location_y / this.imageNaturalHeight) * 100;
    }
    if (
      this.observationDetails.observation_flag === 'observation_on_360' ||
      this.observationDetails.observation_flag === 'observation_on_frame'
    ) {
      dotLeft = (parseFloat(this.observationDetails.point.point_x) / this.imageNaturalWidth) * 100;
      dotTop = (parseFloat(this.observationDetails.point.point_y) / this.imageNaturalHeight) * 100;
    }

    dotElement.style.left = `${dotLeft}%`;
    dotElement.style.top = `${dotTop}%`;
  }

  getX(observationDetails: ObservationDetails): any {
    switch (observationDetails.observation_flag) {
      case 'observation_on_floor_plan':
        return observationDetails.location_x;
      case 'observation_on_360':
        return observationDetails.point.point_x;
      default:
        return observationDetails.location_x;
    }
  }

  getY(observationDetails: ObservationDetails): any {
    switch (observationDetails.observation_flag) {
      case 'observation_on_floor_plan':
        return observationDetails.location_y;
      case 'observation_on_360':
        return observationDetails.point.point_y;
      default:
        return observationDetails.location_y;
    }
  }

  editObservationPrivacy(privacy: any) {
    if (!this.nwc_client) {
      this.isEditing = true;
    }
    this.onEditObservationPrivacy.emit({ privacy: privacy });
    this.observationDetails.privacy = privacy;
    this.changePrivacy = false;
  }

  editObservationTitle(title: any) {
    if (!title || title.trim() === '') {
      this.changeTitle = false;

      if (!this.nwc_client) {
        this.isEditing = false;
      }
    } else {
      if (!this.nwc_client) {
        this.isEditing = true;
      }
      this.onEditObservationTitle.emit({ title: title });
      this.observationDetails.title = title;
      this.changeTitle = false;
    }
  }

  editObservationCategories(categories: any, childCategories: any = null) {
    // Check if categories is provided (can be single value or array)
    const hasCategories = categories && (Array.isArray(categories) ? categories.length > 0 : true);

    if (hasCategories) {
      // Validate "Other" category selection for MIR/WIR types
      if (!this.validateOtherCategorySelection(categories)) {
        this._toaster.error(
          this.translateService.translate(
            'Cannot select "Other" category without specifying "Other Discipline" field',
          ),
        );
        return;
      }

      // Check if subcategories are required when they are available
      if (this.observationChildCategories && this.observationChildCategories.length > 0) {
        const hasChildCategories =
          childCategories && (Array.isArray(childCategories) ? childCategories.length > 0 : true);
        if (!hasChildCategories) {
          this._toaster.error(this.translateService.translate('Sub-category is required'));
          return;
        }
      }

      if (!this.nwc_client) {
        this.isEditing = true;
      }

      // Normalize to arrays if not already (handles both NWC single selection and Non-NWC multi-selection)
      const categoriesArray = Array.isArray(categories) ? categories : [categories];
      const subcategoriesArray = childCategories
        ? Array.isArray(childCategories)
          ? childCategories
          : [childCategories]
        : [];

      // Send categories and subcategories
      this.onEditObservationCategories.emit({
        categories: categoriesArray,
        subcategories: subcategoriesArray,
      });

      this.observationDetails.categories = categoriesArray;
      this.observationDetails.sub_categories = subcategoriesArray;
      this.changeCategories = false;
    } else {
      this._toaster.error(this.translateService.translate('This field cannot be empty'));
    }
  }

  /**
   * Check if observation type requires "Other" category validation
   */
  private requiresOtherCategoryValidation(): boolean {
    if (!this.observationDetails?.type) {
      return false;
    }
    const type = this.observationDetails.type.toUpperCase();
    return ['MIR', 'WIR', 'MIR_LOGS', 'WIR_LOGS'].includes(type);
  }

  /**
   * Check if a category is the "Other" category
   */
  private isOtherCategory(categoryId: any): boolean {
    if (!categoryId || !this.categories) {
      return false;
    }

    const category = this.categories.find((cat) => cat.id === categoryId);
    if (!category) {
      return false;
    }

    // Check if category name is "Other" in either English or Arabic
    return (
      category.category_name_en?.toLowerCase() === 'other' || category.category_name_ar === 'Other'
    );
  }

  /**
   * Validate if "Other" category can be selected
   * Returns true if validation passes, false if validation fails
   *
   * This validation ensures that the user has SAVED a value to the other_discipline field
   * before they can select the "Other" category. It only checks observationDetails.custom_fields
   * which contains the saved/submitted values, NOT form values which might be unsaved user input.
   */
  private validateOtherCategorySelection(categories: any): boolean {
    // Only validate for MIR/WIR/MIR_LOGS/WIR_LOGS types
    if (!this.requiresOtherCategoryValidation()) {
      return true;
    }

    // Normalize to array
    const categoriesArray = Array.isArray(categories) ? categories : [categories];

    // Check if "Other" category is being selected
    const hasOtherCategory = categoriesArray.some((catId) => this.isOtherCategory(catId));

    if (!hasOtherCategory) {
      // "Other" is not selected, validation passes
      return true;
    }

    // "Other" is selected, check if other_discipline field has a SAVED value
    // Only check observationDetails.custom_fields (saved values), not form values
    if (!this.observationDetails?.custom_fields) {
      return false;
    }

    const savedField = this.observationDetails.custom_fields.find(
      (f) => f.name === 'other_discipline',
    );

    if (!savedField) {
      return false;
    }

    if (!savedField.default) {
      return false;
    }

    const value = savedField.default;
    let hasValue = false;

    if (typeof value === 'string') {
      hasValue = value.trim() !== '';
    } else {
      hasValue = !!value;
    }

    if (hasValue) {
      return true;
    }

    return false;
  }

  editObservationAttachments() {
    if (!this.nwc_client) {
      this.isEditing = true;
    }
    this.onEditObservationAttachments.emit({
      attached_files: this.attachments,
    });
    this.changeAttachments = false;

    //reset the attachment so no duplication
    this.attachments = [];
  }

  focusTitle(): void {
    setTimeout(() => {
      this.title?.nativeElement?.focus();
    }, 500);
  }

  focusDescription(): void {
    setTimeout(() => {
      this.description?.nativeElement?.focus();
    }, 500);
  }

  focusRecommendedAction(): void {
    setTimeout(() => {
      this.recommendedAction?.nativeElement?.focus();
    }, 500);
  }

  async attachmentAction(attach: Attachment) {
    if (!this.changeAttachments) {
      this.downloadFile(attach);
    } else {
      if (attach.can_delete == false) {
        return;
      }
      const dialogData = new ConfirmDialogModel(
        this.translateService.translate('Are you sure want to delete this file?'),
        this.translateService.translate(
          'Kindly note that this attachment will be deleted permanently',
        ),
      );
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        maxWidth: '640px',
        minWidth: '300px',
        data: dialogData,
      });
      dialogRef.afterClosed().subscribe(async (dialogResult) => {
        if (dialogResult) {
          const response = await this._project
            .deleteAttached(attach.file_id, this._project?.id)
            .catch((e: HttpErrorResponse) => {
              if (e.error.error) {
                this._toaster.error(e.error.error[0]);
              } else {
                this._toaster.error(e.error.toString());
              }
            });
          if (response) {
            this.observationDetails.attached_files = this.observationDetails.attached_files?.filter(
              (att) => att.file_id !== attach.file_id,
            );
            this.attachments = this.attachments?.filter((att) => att.name !== attach.file_name);
            this._toaster.success(this.translateService.translate('File deleted successfully'));
          }
        }
      });
    }
  }

  async evidenceAction(evidence: string) {
    if (!this.changeEvidenceAttachment) {
      this.downloadEvidence(evidence);
    } else {
      const dialogData = new ConfirmDialogModel(
        this._transloco.translate('Close-Out Evidence'),
        this._transloco.translate(`Kindly note that this attachment will be deleted permanently`),
        false,
        false,
        null,
        this._transloco.translate('Delete'),
      );

      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        maxWidth: '640px',
        minWidth: '300px',
        data: dialogData,
      });
      dialogRef.afterClosed().subscribe(async (dialogResult) => {
        if (dialogResult) {
          const response = await this._project
            .deleteEvidence(this.observationDetails?.id)
            .catch((e: HttpErrorResponse) => {
              if (e.error.error) {
                this._toaster.error(e.error.error[0]);
              } else {
                this._toaster.error(e.error.toString());
              }
            });
          if (response) {
            if (!this.nwc_client) {
              this.isEditing = false;
            }
            this.changeEvidenceAttachment = false;

            this.onEditObservationEvidence.emit('delete');
            if (!this.nwc_client) {
              this.isEditing = false;
            }
            this._toaster.success(this.translateService.translate('File deleted successfully'));
          }
        }
      });
    }
  }

  getUpdatePermission(): boolean {
    if (this.observationDetails?.deleted_at) {
      return false;
    }

    return (
      this.observationDetails?.permissions?.includes(ObservationPermissions.EDIT_OBSERVATION) ??
      false
    );
  }

  getUpdateCodePermission(): boolean {
    if (this.observationDetails?.deleted_at) {
      return false;
    }

    return (
      this.observationDetails?.permissions?.includes(
        ObservationPermissions.EDIT_OBSERVATION_CODE,
      ) ?? false
    );
  }

  get isClosedAndLocked(): boolean {
    const hasChangeStatus = this.observationDetails?.permissions?.includes(
      ObservationPermissions.CHANGE_OBSERVATION_STATUS,
    );
    return !hasChangeStatus && this.observationDetails?.status === 'closed';
  }

  getChangeStatusPermission(
    jsonArray: { text: string; value: boolean }[],
    permissionType: string,
  ): boolean {
    if (this.observationDetails?.deleted_at) {
      return false;
    }
    const permission = jsonArray?.find((item) => item.text === permissionType);
    return permission ? permission.value : false;
  }

  private _setMinDate(): void {
    this.minDate = new Date();
    this.minDate.setUTCHours(0, 0, 0, 0);
  }

  doUpload() {
    if (this.observationDetails?.evidence_file) {
      const dialogData = new ConfirmDialogModel(
        this._transloco.translate('Close-Out Evidence'),
        this._transloco.translate(`The recently uploaded file will replace the existed one`),
        false,
        false,
        null,
        this._transloco.translate('Save'),
      );

      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        maxWidth: '640px',
        minWidth: '300px',
        data: dialogData,
      });

      dialogRef.afterClosed().subscribe(async (res) => {
        if (res) {
          if (!this.nwc_client) {
            this.isEditing = true;
          }
          this.changeEvidenceAttachment = false;
          this.onEditObservationEvidence.emit({
            evidence_file: this.evidenceAttachment,
          });

          if (!this.nwc_client) {
            this.isEditing = false;
          }
        }
        //reset the attachment so no duplication
      });
    } else {
      if (!this.nwc_client) {
        this.isEditing = true;
      }
      this.changeEvidenceAttachment = false;
      this.onEditObservationEvidence.emit({
        evidence_file: this.evidenceAttachment,
      });

      if (!this.nwc_client) {
        this.isEditing = false;
      }
    }
  }

  getChangeEvidencePermission(
    jsonArray: { text: string; value: boolean }[],
    permissionType: string,
  ): boolean {
    if (this.observationDetails?.deleted_at) {
      return false;
    }
    const permission = jsonArray?.find((item) => item.text === permissionType);
    return permission ? permission.value : false;
  }

  getFileName(filePath: string): string {
    const fileName = filePath.substring(filePath.lastIndexOf('/') + 1);
    const maxLength = 15; // Customize this if necessary
    const truncated =
      fileName.length > maxLength ? fileName.substring(0, maxLength) + '...' : fileName;

    return truncated;
  }

  getFileExtension(fileUrl: string): string | null {
    if (!fileUrl) return null;

    const fileName = fileUrl.substring(fileUrl.lastIndexOf('/') + 1); // Extract the file name from the URL
    const fileExtension = fileName.split('.').pop(); // Get the extension by splitting by the dot and taking the last part

    // Return the file extension if valid, or null if not found
    return fileExtension ? fileExtension.toLowerCase() : null;
  }
  openPreviewer(fileSrc: string, extension?: string, fileName?: string) {
    if (!fileName) {
      fileName = this.getFileName(fileSrc);
    }
    if (!extension) {
      extension = this.getFileExtension(fileSrc);
    }
    this.dialog.open(FilePreviewerDialogComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw', // Ensure the dialog doesn't shrink
      maxHeight: '100vh', // Ensure the dialog doesn't shrink
      panelClass: 'full-screen-dialog', // Custom class for additional styling
      data: { fileSrc: fileSrc, extension: extension, fileName: fileName },
    });
  }

  /** PPE auto-detection (V2CORE-2901): the observation was created by the AI. */
  get isAiObservation(): boolean {
    return isAiGenerated(this.observationDetails);
  }

  /** PPE auto-detection (V2CORE-2901): AI confidence as a whole percentage, null when missing. */
  get aiConfidencePercent(): number | null {
    return ppeConfidencePercent(this.observationDetails?.ppe_detection_confidence);
  }

  get noImagePlaceholderPath(): string {
    const lang = this.translateService.getActiveLang() || 'en';
    return getObservationPlaceholderImage(lang, this.nwc_client);
  }

  onImageError(event: Event) {
    (event.target as HTMLImageElement).src = this.noImagePlaceholderPath;
  }

  editObservationCode(value: any) {
    if (!value) {
      this.changeCode = false;
      if (!this.nwc_client) {
        this.isEditing = false;
      }
      return;
    }
    if (!this.nwc_client) {
      this.isEditing = true;
    }
    let code = this.observationCodes?.find((item) => item.value === value);
    this.onEditObservationCode.emit({ type: value });
    this.observationDetails.code_label = code?.label;

    this.changeCode = false;
    if (!this.nwc_client) {
      this.isEditing = false;
    }
  }

  isArabic() {
    let currentLang = localStorage.getItem('lang');
    return currentLang === 'ar';
  }

  /**
   * Returns the category (or sub-category) display name for the active language.
   * Uses category_name_ru for Russian, category_name_ar for Arabic, else category_name_en.
   */
  getCategoryDisplayName(cat: {
    category_name_ar?: string;
    category_name_en?: string;
    category_name_ru?: string;
  }): string {
    if (!cat) return '';
    const lang = this._transloco.getActiveLang() || 'en';
    if (lang === 'ar' && cat.category_name_ar) return cat.category_name_ar;
    if (lang === 'ru' && cat.category_name_ru) return cat.category_name_ru;
    return cat.category_name_en ?? cat.category_name_ar ?? cat.category_name_ru ?? '';
  }

  onEditorCreated(quill: Quill, editorType?: 'description' | 'recommendedAction') {
    // Store the editor reference based on type
    if (editorType === 'description') {
      this.descriptionEditor = quill;
    } else if (editorType === 'recommendedAction') {
      this.recommendedActionEditor = quill;
    }

    // Use QuillEditorService to setup the editor
    if (editorType && this.editorConfig) {
      this.quillEditorService.setupEditor(quill, editorType, this.editorConfig);
    }
  }

  openDialog(type: string): void {
    this.dialogRef = this.dialog.open(
      type == 'desc' ? this.dialogTemplateDesc : this.dialogTemplateRecc,
      { width: '600px', disableClose: true },
    );
  }

  /**
   * Load custom fields for the selected observation type
   */
  setupCustomFields(): void {
    this.isLoadingCustomFields = true;
    const loadVersion = ++this.customFieldsLoadVersion;
    this.customFieldsSubscription?.unsubscribe();

    const detailsCustomFields = this.getDetailsCustomFieldsWithValues();

    // Create a temporary form with project_id for loading custom fields
    const tempForm = new UntypedFormGroup({ project_id: new UntypedFormControl(this._project.id) });

    this.customFieldsObservable = this.customFieldRenderer.loadCustomFieldsForObservationType(
      this.observationDetails?.type,
      tempForm,
      async (fields: CustomField[]) => {
        if (loadVersion !== this.customFieldsLoadVersion) {
          return;
        }

        const fieldsWithValues = this.mergeCustomFieldsForDetails(fields, detailsCustomFields);

        this.customFields = fieldsWithValues;

        // Initialize the custom fields form with metadata and value change listeners
        this.initCustomFieldsForm();

        this.isLoadingCustomFields = false;
      },
      (error) => {
        if (loadVersion !== this.customFieldsLoadVersion) {
          return;
        }

        console.error('Error loading custom fields:', error);
        this.customFields = detailsCustomFields;

        this.initCustomFieldsForm();
        this.isLoadingCustomFields = false;
      },
      // Pass the existing observation id so creation-locked fields (e.g. NCR Type)
      // are returned by the BE as read_only=true in details/edit mode.
      this.observationDetails?.id,
    );

    this.customFieldsSubscription = this.customFieldsObservable.subscribe();
  }

  private getDetailsCustomFieldsWithValues(): CustomFieldWithValue[] {
    const detailsCustomFields = this.observationDetails?.custom_fields;

    if (!Array.isArray(detailsCustomFields) || detailsCustomFields.length === 0) {
      return [];
    }

    return this.mapCustomFieldsWithCurrentValues(detailsCustomFields);
  }

  private mergeCustomFieldsForDetails(
    endpointFields: CustomField[],
    detailsFields: CustomFieldWithValue[],
  ): CustomFieldWithValue[] {
    if (!Array.isArray(endpointFields) || endpointFields.length === 0) {
      return detailsFields;
    }

    if (!Array.isArray(detailsFields) || detailsFields.length === 0) {
      return this.mapCustomFieldsWithCurrentValues(endpointFields);
    }

    const detailsByName = new Map(detailsFields.map((field) => [field.name, field]));
    const endpointFieldNames = new Set(endpointFields.map((field) => field.name));

    const mergedEndpointFields = endpointFields.map((endpointField) =>
      this.mergeEndpointFieldWithDetails(endpointField, detailsByName.get(endpointField.name)),
    );
    const detailsOnlyFields = detailsFields.filter((field) => !endpointFieldNames.has(field.name));

    return [...mergedEndpointFields, ...detailsOnlyFields];
  }

  private mergeEndpointFieldWithDetails(
    endpointField: CustomField,
    detailsField?: CustomFieldWithValue,
  ): CustomFieldWithValue {
    if (!detailsField) {
      return {
        ...endpointField,
        currentValue: this.getCurrentCustomFieldValue(endpointField),
      };
    }

    const detailsFieldValue = this.getValueFromCustomField(detailsField);

    const mergedField: CustomFieldWithValue = {
      ...endpointField,
      required: detailsField.required ?? endpointField.required,
      read_only: Boolean(detailsField.read_only || endpointField.read_only),
      default: detailsFieldValue,
      currentValue: detailsFieldValue,
      field_validation: detailsField.field_validation ?? endpointField.field_validation,
      validations: detailsField.validations ?? endpointField.validations,
    };

    return mergedField;
  }

  private mapCustomFieldsWithCurrentValues(fields: CustomField[]): CustomFieldWithValue[] {
    return fields.map((field) => ({
      ...field,
      currentValue: this.getCurrentCustomFieldValue(field),
    }));
  }

  private getCurrentCustomFieldValue(field: CustomField): any {
    const detailsField = this.observationDetails?.custom_fields?.find(
      (item) => item.name === field.name,
    );

    if (detailsField && detailsField.default !== undefined) {
      return detailsField.default;
    }

    if ((field as CustomFieldWithValue).currentValue !== undefined) {
      return (field as CustomFieldWithValue).currentValue;
    }

    return null;
  }

  private getValueFromCustomField(field: CustomFieldWithValue): any {
    if (field.default !== undefined) {
      return field.default;
    }

    if (field.currentValue !== undefined) {
      return field.currentValue;
    }

    return null;
  }

  /**
   * Track by function for custom fields
   */
  trackByField = (index: number, field: CustomField): string => {
    return this.observationCustomFields.trackByField(index, field);
  };

  /**
   * Check if a custom field should be rendered based on:
   * 1. Dependency satisfaction (if it has depends_on)
   * 2. Control existence in the form
   */
  shouldRenderCustomField(field: CustomField): boolean {
    if (!this.customFieldsForm) {
      return false;
    }

    // Check if control exists in the form using get() instead of contains()
    // Note: contains() may return false for disabled controls, so we use get()
    const control = this.customFieldsForm.get(field.name);
    if (!control) {
      return false;
    }

    // Check if dependency is satisfied (if field has dependency)
    if (field.depends_on) {
      return this.customFieldRenderer.isDependencySatisfied(this.customFieldsForm, field);
    }

    return true;
  }

  /**
   * Get all visible custom fields
   * Only returns base-level fields (not nested children)
   * This is called by the template but should be stable - returns same fields for same form state
   */
  get visibleCustomFields(): CustomFieldWithValue[] {
    if (!this.customFields || !this.customFieldsForm) {
      return [];
    }

    // Only return base-level fields (not nested children)
    // Nested children will be handled by their parent fields
    return this.customFields.filter((field) => {
      // Only show fields that:
      // 1. Have a control in the form
      // 2. Meet their dependency requirements
      // 3. Are not child fields (child fields are rendered by their parents)
      return this.shouldRenderCustomField(field) && !this.isChildField(field);
    });
  }

  /**
   * Check if a field is a child field (nested under a parent select field)
   */
  private isChildField(field: CustomField): boolean {
    if (!this.customFields) {
      return false;
    }

    // Check if any other field has this field as a child
    return this.customFields.some((parentField) => {
      if (parentField.type === 'select' && parentField.options) {
        return parentField.options.some((option) => option.child?.name === field.name);
      }
      return false;
    });
  }

  /**
   * Get child field for a specific parent field based on current value
   */
  getChildFieldForParent(parentField: CustomField): CustomFieldWithValue | null {
    if (!this.customFieldsForm || parentField.type !== 'select') {
      return null;
    }

    const currentValue = this.customFieldsForm.get(parentField.name)?.value;
    if (!currentValue) {
      return null;
    }

    const childField = this.customFieldRenderer.findChildField(parentField, currentValue);
    if (!childField) {
      return null;
    }

    // Check if child field control exists in form and should render
    if (
      this.customFieldsForm.contains(childField.name) &&
      this.customFieldRenderer.isDependencySatisfied(this.customFieldsForm, childField)
    ) {
      // Convert to CustomFieldWithValue
      return {
        ...childField,
        currentValue: this.customFieldsForm.get(childField.name)?.value,
      };
    }

    return null;
  }

  /**
   * Check if a parent field has a visible child field
   */
  hasVisibleChildField(parentField: CustomField): boolean {
    return this.getChildFieldForParent(parentField) !== null;
  }

  /**
   * Get option label for select fields
   */
  getOptionLabel(field: CustomField, value: any): string {
    if (!field.options || !value) return '';

    const option = field.options.find((opt) => opt.value === value);
    return option ? option.label : '';
  }

  /**
   * Initialize custom fields form
   */
  initCustomFieldsForm(): void {
    this.customFieldsForm = this.fb.group({}) as UntypedFormGroup & CustomFieldsFormGroup;

    // Add metadata for dependency resolution (categories for custom field dependencies)
    const categoriesOptions = this.categories?.map((category) => ({
      label: this.getCategoryDisplayName(category),
      value: category.id,
      children: category.children,
    }));

    if (categoriesOptions) {
      this.customFieldsForm.metadata = {
        categories: categoriesOptions,
      };
    }

    // Add virtual controls for dependency resolution
    // Custom fields may depend on observation properties like categories
    // We add these as form controls so dependencies can be resolved
    this.customFieldsForm.addControl('categories', new UntypedFormControl(this.selectedCategories));
    this.customFieldsForm.addControl('type', new UntypedFormControl(this.observationDetails?.type));

    // Ensure custom fields are available before adding controls
    if (this.customFields && this.customFields.length > 0) {
      // Use the service to properly add form controls with validation
      this.customFieldRenderer.addCustomFieldsToForm(this.customFieldsForm, this.customFields);

      // Set the current values after creating the controls
      this.customFields.forEach((field) => {
        const control = this.customFieldsForm.get(field.name);
        if (control) {
          control.setValue(field.currentValue);
        }
      });

      // Setup value change listeners for parent fields to handle dependencies
      this.setupCustomFieldValueChangeListeners();
    }

    // Process parent log fields to identify non-editable standard observation fields
    if (
      this.observationDetails.type === 'MIR_LOGS' ||
      this.observationDetails.type === 'WIR_LOGS'
    ) {
      this.processParentLogFields();
      // Apply additional non-editable fields for WIR_LOGS and MIR_LOGS
      this.applyWirLogsNonEditableFields();
      this.applyMirLogsNonEditableFields();
    }
  }

  /**
   * Process parent log fields (mir_logs_parent or wir_logs_parent)
   * to identify and mark related STANDARD OBSERVATION FIELDS as non-editable
   * Note: Custom fields use read_only property directly, so we only track standard fields here
   */
  private processParentLogFields(): void {
    // Clear the set before processing
    this.nonEditableFields.clear();

    // Find parent log fields
    const parentLogFields =
      this.customFields?.filter(
        (field) => field.name === 'mir_logs_parent' || field.name === 'wir_logs_parent',
      ) || [];

    if (parentLogFields.length === 0) {
      return;
    }

    // Get all custom field names for filtering
    const customFieldNames = new Set(this.customFields?.map((f) => f.name) || []);

    // Process each parent log field
    parentLogFields.forEach((parentField) => {
      if (!parentField.options || parentField.options.length === 0) {
        return;
      }

      // All options have same related_fields
      const matchingOption = parentField.options[0];

      if (!matchingOption || !matchingOption.related_fields) {
        return;
      }

      // Mark only STANDARD OBSERVATION FIELDS as non-editable
      // Custom fields are handled by their read_only property
      matchingOption.related_fields.forEach((relatedField: any) => {
        if (relatedField.field_name) {
          // Only add to nonEditableFields if it's NOT a custom field
          if (!customFieldNames.has(relatedField.field_name)) {
            this.nonEditableFields.add(relatedField.field_name);
          }
        }
      });
    });
  }

  /**
   * Apply additional non-editable fields for WIR_LOGS observations
   * These fields are editable in WIR but read-only in WIR_LOGS
   */
  private applyWirLogsNonEditableFields(): void {
    if (this.observationDetails.type !== 'WIR_LOGS') {
      return;
    }

    const config = SharedObservationDetailsComponent.WIR_LOGS_NON_EDITABLE_FIELDS;

    // Add all fields to nonEditableFields Set
    config.all.forEach((field) => {
      this.nonEditableFields.add(field);
    });
  }

  /**
   * Apply additional non-editable fields for MIR_LOGS observations
   * These fields are editable in MIR but read-only in MIR_LOGS
   */
  private applyMirLogsNonEditableFields(): void {
    if (this.observationDetails.type !== 'MIR_LOGS') {
      return;
    }

    const config = SharedObservationDetailsComponent.MIR_LOGS_NON_EDITABLE_FIELDS;

    // Add all fields to nonEditableFields Set
    config.all.forEach((field) => {
      this.nonEditableFields.add(field);
    });
  }

  /**
   * Setup value change listeners for custom fields to handle dependencies
   * This monitors parent fields and updates child/dependent fields accordingly
   * Angular's reactive forms will automatically trigger change detection
   */
  private setupCustomFieldValueChangeListeners(): void {
    if (!this.customFields || this.customFields.length === 0 || !this.customFieldsForm) {
      return;
    }

    // For each field, check if it has dependencies and set up watchers
    this.customFields.forEach((field) => {
      // Watch for dependency parent changes
      if (field.depends_on) {
        const parentControl = this.customFieldsForm.get(field.depends_on.field);
        if (parentControl) {
          parentControl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
            // Apply dependency state (show/hide field based on parent value)
            // Angular will automatically detect the form control changes
            this.customFieldRenderer.applyDependencyState(this.customFieldsForm, field);
          });

          // Apply initial dependency state
          this.customFieldRenderer.applyDependencyState(this.customFieldsForm, field);
        }
      }

      // Watch for select field changes to handle child fields (nested fields)
      if (field.type === 'select' && field.options && field.options.length > 0) {
        const control = this.customFieldsForm.get(field.name);
        if (control) {
          let previousValue: string | null = control.value;

          control.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe((newValue) => {
            // Handle parent field change - this will add/remove child fields as needed
            // Angular will automatically detect the form control changes
            this.customFieldRenderer.handleParentFieldChange(
              this.customFieldsForm,
              field,
              newValue,
              previousValue || undefined,
            );

            previousValue = newValue;
          });

          // Initialize child field if there's already a value
          if (previousValue) {
            const childField = this.customFieldRenderer.findChildField(field, previousValue);
            if (childField && !this.customFieldsForm.contains(childField.name)) {
              this.customFieldRenderer.addChildFieldToForm(this.customFieldsForm, childField);
            }
          }
        }
      }
    });
  }

  /**
   * Re-evaluate all custom field dependencies
   * Called when external dependencies change (e.g., categories)
   * Angular's reactive forms will automatically trigger change detection
   */
  private reEvaluateCustomFieldDependencies(): void {
    if (!this.customFields || !this.customFieldsForm) {
      return;
    }

    // Get all custom fields including any that may have been added dynamically (child fields)
    const registeredFields = this.customFieldRenderer.getRegisteredFields(this.customFieldsForm);
    const fieldsToCheck = registeredFields.length > 0 ? registeredFields : this.customFields;

    // Re-apply dependency state for all fields
    // Angular will automatically detect the form control changes
    fieldsToCheck.forEach((field: CustomField) => {
      if (field.depends_on) {
        this.customFieldRenderer.applyDependencyState(this.customFieldsForm, field);
      }
    });
  }

  /**
   * Handle custom field editing
   */
  editCustomFields(): void {
    this.initCustomFieldsForm();
    if (!this.nwc_client) {
      this.isEditing = true;
    }
  }

  /**
   * Start editing a specific custom field
   */
  startEditingField(fieldName: string): void {
    const field = this.customFields.find((item) => item.name === fieldName);
    if (field?.read_only || this.isFieldNonEditable(fieldName)) {
      return;
    }

    if (!this.customFieldsForm || this.editingFields.size === 0) {
      this.initCustomFieldsForm();
    }

    this.editingFields.add(fieldName);
    if (!this.nwc_client) {
      this.isEditing = true;
    }
  }

  /**
   * Stop editing a specific custom field
   */
  stopEditingField(fieldName: string): void {
    this.editingFields.delete(fieldName);
    if (!this.nwc_client && this.editingFields.size === 0) {
      this.isEditing = false;
    }
  }

  /**
   * Check if a specific field is being edited
   */
  isFieldEditing(fieldName: string): boolean {
    return this.editingFields.has(fieldName);
  }

  /**
   * Check if a specific field is non-editable
   * - For custom fields: Check the read_only property from API
   * - For standard observation fields: Check if in nonEditableFields Set (from related_fields)
   */
  isFieldNonEditable(fieldName: string): boolean {
    return this.nonEditableFields.has(fieldName);
  }

  /**
   * Cancel custom fields editing
   */
  cancelCustomFields(): void {
    this.editingFields.clear();

    if (!this.nwc_client) {
      this.isEditing = false;
    }
  }

  /**
   * Check if a specific custom field is valid for saving
   */
  isCustomFieldValid(fieldName: string): boolean {
    if (!this.customFieldsForm || !this.customFieldsForm.get(fieldName)) {
      return false;
    }

    const control = this.customFieldsForm.get(fieldName);
    const field = this.customFields.find((f) => f.name === fieldName);

    if (!control || !field) {
      return false;
    }

    // Check Angular form control validity first (includes min/max validators)
    if (control.invalid) {
      return false;
    }

    // Use the existing validation service for additional custom validation
    const validationResult = this.customFieldRenderer.validateFieldValue(field, control.value);
    return validationResult.valid;
  }

  /**
   * Get validation error message for a specific custom field
   */
  getCustomFieldErrorMessage(fieldName: string): string {
    if (!this.customFieldsForm || !this.customFieldsForm.get(fieldName)) {
      return '';
    }

    const control = this.customFieldsForm.get(fieldName);
    const field = this.customFields.find((f) => f.name === fieldName);

    if (!control || !field) {
      return '';
    }

    // Use the existing validation service
    const validationResult = this.customFieldRenderer.validateFieldValue(field, control.value);
    return validationResult.errors.length > 0 ? validationResult.errors[0] : '';
  }

  /**
   * Save a specific custom field
   */
  saveCustomField(fieldName: string): void {
    // Validate the field before saving
    if (!this.isCustomFieldValid(fieldName)) {
      return;
    }

    if (this.customFieldsForm && this.customFieldsForm.get(fieldName)) {
      const value = this.customFieldsForm.get(fieldName)?.value;

      // Update the field's current value
      const field = this.customFields.find((f) => f.name === fieldName);
      if (field) {
        field.currentValue = value;
      }

      // Emit the change for this specific field
      const customFieldValues = { [fieldName]: value };
      this.onEditCustomFields.emit(customFieldValues);

      // Stop editing this field
      this.stopEditingField(fieldName);
    }
  }

  /**
   * Cancel editing a specific custom field
   */
  cancelCustomField(fieldName: string): void {
    // Reset the form control to its original value
    const field = this.customFields.find((f) => f.name === fieldName);
    if (field && this.customFieldsForm) {
      this.customFieldsForm.get(fieldName)?.setValue(field.currentValue);
    }

    // Stop editing this field
    this.stopEditingField(fieldName);
  }

  /**
   * Handle individual custom field changes
   * Angular's reactive forms will automatically trigger change detection
   */
  onEditCustomField(event: { fieldName: string; value: any }): void {
    // Update the form control value
    // Angular will automatically detect this change and update the button state
    if (this.customFieldsForm) {
      this.customFieldsForm.get(event.fieldName)?.setValue(event.value);
    }
  }

  getDescriptionCharCount(): number | null {
    if (!this.descriptionEditor || !this.editorConfig) {
      return null;
    }

    const observationType = this.editorConfig?.observationType;
    if (!observationType) {
      return null;
    }

    return this.quillEditorService.getTextCharCount(
      this.descriptionEditor,
      'description',
      this.editorConfig,
    );
  }

  getRecommendedActionCharCount(): number | null {
    if (!this.recommendedActionEditor || !this.editorConfig) {
      return null;
    }

    const observationType = this.editorConfig?.observationType;
    if (!observationType) {
      return null;
    }

    return this.quillEditorService.getTextCharCount(
      this.recommendedActionEditor,
      'recommendedAction',
      this.editorConfig,
    );
  }

  /**
   * Get max length for description editor display
   */
  getDescriptionMaxLength(): number | null {
    if (!this.editorConfig) {
      return null;
    }
    const observationType = this.editorConfig?.observationType;
    if (!observationType) {
      return null;
    }
    const limits = this.quillEditorService.getEditorLimits(this.editorConfig, 'description');
    return limits.maxTextLength;
  }

  /**
   * Get text limits for description editor display (alias for getDescriptionMaxLength)
   */
  getDescriptionTextLimits(): number | null {
    return this.getDescriptionMaxLength();
  }

  /**
   * Get max length for recommended action editor display
   */
  getRecommendedActionMaxLength(): number | null {
    if (!this.editorConfig) {
      return null;
    }
    const observationType = this.editorConfig?.observationType;
    if (!observationType) {
      return null;
    }
    const limits = this.quillEditorService.getEditorLimits(this.editorConfig, 'recommendedAction');
    return limits.maxTextLength;
  }

  /**
   * Get text limits for recommended action editor display (alias for getRecommendedActionMaxLength)
   */
  getRecommendedActionTextLimits(): number | null {
    return this.getRecommendedActionMaxLength();
  }

  getStatusClass(isDraft: boolean, status: string): string {
    if (isDraft) {
      return 'bg-yellow-500';
    }

    // Map status to CSS class
    const statusClassMap: { [key: string]: string } = {
      pending: 'pending',
      in_progress: 'inProgress',
      'in progress': 'inProgress',
      completed: 'completed',
      closed: 'closed',
      close: 'closed',
      deleted_at: 'deleted_at',
      'deleted at': 'deleted_at',
      opened: 'opened',
      open: 'opened',
      reviewed: 'reviewed',
      rejected: 'rejected',
      approved: 'approved',
    };

    return statusClassMap[status] || 'default-status';
  }

  /**
   * Helper method to start editing mode
   * For NWC clients, this does nothing (allows multiple field editing)
   * For non-NWC clients, this sets isEditing to true (disables other edit buttons)
   */
  startEditing(): void {
    if (!this.nwc_client) {
      this.isEditing = true;
    }
  }

  /**
   * Helper method to stop editing mode
   * For NWC clients, this does nothing
   * For non-NWC clients, this sets isEditing to false (enables other edit buttons)
   */
  stopEditing(): void {
    if (!this.nwc_client) {
      this.isEditing = false;
    }
  }

  /**
   * Check if Sync Status should be displayed for the current observation
   * Only displays for NWC clients
   */
  shouldShowSyncStatus(): boolean {
    return this.nwc_client && !!this.observationDetails;
  }

  /**
   * Check if the "View Related Logs" button should be shown
   * Only shown for MIR/WIR parent observations with sequence numbers
   */
  shouldShowRelatedLogsButton(): boolean {
    if (!this.observationDetails) return false;

    const type = (
      this.observationDetails.type_label || this.observationDetails.type
    )?.toUpperCase();
    return this.nwc_client && ['MIR', 'WIR'].includes(type);
  }

  /**
   * Open the related logs dialog
   */
  openRelatedLogsDialog(): void {
    if (!this.shouldShowRelatedLogsButton()) return;

    if (!this.observationDetails.logs_button?.logs_count) {
      return;
    }

    const logType = (
      this.observationDetails.type_label || this.observationDetails.type
    )?.toUpperCase();

    this.dialog.open(RelatedLogsDialogComponent, {
      width: '90vw',
      maxWidth: '1200px',
      maxHeight: '80vh',
      data: {
        sequenceNumber: this.observationDetails.unifier_sequence_number,
        logType: logType,
        parentRecordNo: this.observationDetails.unifier_observation_id,
        projectId: this.observationDetails.project?.project_id || this.projectId,
      },
    });
  }
}
